import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Hammer, Handshake } from "lucide-react";
import SEO from "@/components/SEO";

// Real Unsplash photography of prestigious properties
const HERO_IMG =
  "https://images.unsplash.com/photo-1648475236583-2e25a6cbf3bd?auto=format&fit=crop&w=2400&q=85";

const services = [
  {
    to: "/ristrutturazioni",
    num: "01",
    title: "Ristrutturazioni",
    desc: "Appartamenti, uffici, negozi e interi fabbricati. Progetto interno, direzione lavori, chiavi in mano.",
    img: "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?auto=format&fit=crop&w=1400&q=85",
    testId: "home-service-ristrutturazioni",
  },
  {
    to: "/edificazioni",
    num: "02",
    title: "Edificazioni & Ricostruzioni",
    desc: "Demolizione e ricostruzione, edificazione ex novo, interventi strutturali con strutturisti interni.",
    img: "https://images.unsplash.com/photo-1602757115429-b4190ae087be?auto=format&fit=crop&w=1400&q=85",
    testId: "home-service-edificazioni",
  },
  {
    to: "/operazioni-immobiliari",
    num: "03",
    title: "Operazioni Immobiliari",
    desc: "Diventa imprenditore immobiliare con il partner giusto: partnership, permuta, vendita diretta.",
    img: "https://images.unsplash.com/photo-1526948531399-320e7e40f0ca?auto=format&fit=crop&w=1400&q=85",
    testId: "home-service-operazioni",
  },
];

export default function Home() {
  return (
    <div className="grain-overlay">
      <SEO
        title="Impresa edile a Napoli — Ristrutturazioni, Edificazioni e Investimenti Immobiliari | Ri.Sco."
        description="Ri.Sco. s.r.l. — impresa edile a Napoli e provincia. Ristrutturazioni chiavi in mano, edificazioni, demolizioni e operazioni immobiliari con team interno. Analizzatore di Progetto AI e sopralluogo gratuito."
        path="/"
      />
      {/* HERO — comunica subito le due anime */}
      <section className="relative min-h-screen flex items-end overflow-hidden -mt-20 pt-20" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Interior design premium Ri.Sco." className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-risco-dark/95 via-risco-dark/50 to-risco-dark/20" />
        </div>
        <div className="relative container-x pb-20 md:pb-28 w-full">
          <div className="mb-6" data-testid="hero-overline">
            <div
              className="font-heading font-semibold text-risco-bg text-3xl sm:text-4xl md:text-5xl tracking-tight leading-none"
              data-testid="hero-brand"
            >
              Ri.Sco. <span className="font-display italic text-risco-accent">s.r.l.</span>
            </div>
            <div className="mt-3 text-stone-300 text-xs uppercase tracking-[0.28em]">
              Impresa edile · Napoli
            </div>
          </div>
          <h1 className="text-risco-bg leading-[0.95] max-w-6xl">
            <span className="font-heading font-medium text-4xl sm:text-5xl md:text-6xl lg:text-8xl block tracking-tighter">
              Un'impresa edile.
            </span>
            <span className="font-display italic text-4xl sm:text-5xl md:text-6xl lg:text-8xl block mt-1">
              Un partner d'investimento.
            </span>
          </h1>
          <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
            <p className="lg:col-span-7 text-stone-200 text-lg leading-relaxed max-w-2xl">
              Ristrutturiamo e costruiamo con team interno completo — architetti, geometri, strutturisti.
              <span className="font-display italic text-risco-bg"> E investiamo insieme a te</span> su palazzi, terreni e immobili da valorizzare.
            </p>
            <div className="lg:col-span-5 flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link
                to="/simulatore"
                data-testid="hero-cta-simulatore"
                className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-risco-accent hover:bg-risco-accent-dark text-white font-medium transition-colors whitespace-nowrap"
              >
                Analizza il tuo progetto
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/operazioni-immobiliari"
                data-testid="hero-cta-proponi"
                className="inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border border-stone-400/50 hover:bg-risco-bg/10 text-risco-bg font-medium transition-colors whitespace-nowrap"
              >
                Proponi il tuo affare
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DUE ANIME — subito dopo il hero */}
      <section className="py-24 md:py-32" data-testid="home-two-souls">
        <div className="container-x">
          <div className="overline mb-8">Chi siamo · Due anime, un solo team</div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
            {/* Anima 1: impresa edile */}
            <Link to="/ristrutturazioni" data-testid="soul-impresa" className="group relative overflow-hidden rounded-sm min-h-[420px] md:min-h-[520px] flex">
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1800&q=85"
                alt="Impresa edile Ri.Sco."
                className="absolute inset-0 w-full h-full object-cover card-img"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-risco-dark/95 via-risco-dark/50 to-risco-dark/10" />
              <div className="relative flex flex-col justify-between p-8 md:p-12 w-full">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Hammer className="w-5 h-5 text-risco-bg" />
                    <span className="text-xs uppercase tracking-[0.28em] text-risco-bg">01 · Impresa Edile</span>
                  </div>
                  <div className="w-11 h-11 rounded-full border border-risco-bg/50 flex items-center justify-center group-hover:bg-risco-bg group-hover:border-risco-bg transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-risco-bg group-hover:text-risco-ink transition-colors" />
                  </div>
                </div>
                <div>
                  <h2 className="text-risco-bg leading-[0.95]">
                    <span className="font-heading font-medium text-3xl md:text-5xl lg:text-6xl block tracking-tighter">Ristrutturiamo</span>
                    <span className="font-display italic text-3xl md:text-5xl lg:text-6xl block">& costruiamo.</span>
                  </h2>
                  <p className="mt-5 text-stone-200 max-w-md leading-relaxed">
                    Ristrutturazioni, edificazioni, demolizioni e ricostruzioni.
                    Progetto architettonico interno, direzione lavori, chiavi in mano.
                  </p>
                </div>
              </div>
            </Link>

            {/* Anima 2: partner d'investimento */}
            <Link to="/operazioni-immobiliari" data-testid="soul-partner" className="group relative overflow-hidden rounded-sm min-h-[420px] md:min-h-[520px] flex bg-risco-ink">
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1800&q=85"
                alt="Partner d'investimento immobiliare"
                className="absolute inset-0 w-full h-full object-cover opacity-60 card-img"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-risco-ink/95 via-risco-ink/70 to-risco-ink/40" />
              <div className="relative flex flex-col justify-between p-8 md:p-12 w-full">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Handshake className="w-5 h-5 text-risco-accent" />
                    <span className="text-xs uppercase tracking-[0.28em] text-risco-accent">02 · Partner d'Investimento</span>
                  </div>
                  <div className="w-11 h-11 rounded-full border border-risco-accent/60 flex items-center justify-center group-hover:bg-risco-accent group-hover:border-risco-accent transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-risco-accent group-hover:text-white transition-colors" />
                  </div>
                </div>
                <div>
                  <h2 className="text-risco-bg leading-[0.95]">
                    <span className="font-heading font-medium text-3xl md:text-5xl lg:text-6xl block tracking-tighter">Investiamo</span>
                    <span className="font-display italic text-3xl md:text-5xl lg:text-6xl block">insieme a te.</span>
                  </h2>
                  <p className="mt-5 text-stone-300 max-w-md leading-relaxed">
                    Partnership di riqualificazione, permuta, vendita diretta,
                    edificazione condivisa. Trasformiamo il tuo immobile in un'operazione.
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 md:py-32 border-t border-risco-border" data-testid="home-manifesto">
        <div className="container-x">
          <div className="max-w-5xl">
            <div className="overline mb-8">Manifesto</div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-risco-ink">
              Un solo interlocutore per <span className="font-display italic text-risco-accent">architetto</span>,
              geometra, strutturista e impresa.
              <span className="text-stone-500"> In Ri.Sco. sono tutti sotto lo stesso tetto. Tempi certi, costi controllati, nessun coordinamento esterno.</span>
            </h2>
          </div>
        </div>
      </section>

      {/* SERVIZI — 3 aree */}
      <section className="py-24 md:py-32 border-t border-risco-border" data-testid="home-services">
        <div className="container-x">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
            <div>
              <div className="overline mb-3">Aree di lavoro</div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-risco-ink">Cosa facciamo</h2>
            </div>
            <div className="text-sm text-stone-500 max-w-xs">
              Dal primo sopralluogo alla consegna delle chiavi.
              Dalla valutazione all'operazione immobiliare finalizzata.
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {services.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={s.testId}
                className="risco-card-plain group"
              >
                <div className="card-img-wrap aspect-[4/5] bg-stone-200">
                  <img src={s.img} alt={s.title} className="card-img w-full h-full object-cover" />
                </div>
                <div className="pt-6 flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs text-stone-500 font-medium">{s.num}</div>
                    <h3 className="font-heading text-2xl md:text-3xl text-risco-ink mt-2 tracking-tight">{s.title}</h3>
                    <p className="text-stone-600 text-sm mt-3 leading-relaxed max-w-sm">{s.desc}</p>
                  </div>
                  <div className="w-11 h-11 rounded-full border border-stone-400 flex items-center justify-center shrink-0 group-hover:bg-risco-ink group-hover:border-risco-ink transition-colors">
                    <ArrowUpRight className="w-4 h-4 text-risco-ink group-hover:text-risco-bg transition-colors" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DOUBLE CTA */}
      <section className="py-24 md:py-32 border-t border-risco-border" data-testid="home-double-cta">
        <div className="container-x grid md:grid-cols-2 gap-6 lg:gap-8">
          <Link
            to="/simulatore"
            data-testid="double-cta-simulatore"
            className="group border border-risco-ink hover:bg-risco-ink hover:text-risco-bg p-10 md:p-14 rounded-sm transition-colors flex flex-col"
          >
            <div className="text-xs uppercase tracking-[0.28em] text-risco-accent mb-6">Simulatore AI</div>
            <h3 className="font-heading text-3xl md:text-5xl tracking-tight">Preventivo Smart in <span className="font-display italic">90 secondi</span></h3>
            <p className="mt-6 text-stone-600 group-hover:text-stone-300 max-w-md">Rispondi a poche domande. Un modello AI ti restituisce una stima di fascia con voci principali.</p>
            <div className="mt-auto pt-10 flex items-center gap-3 text-sm font-medium">
              Inizia la simulazione <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </Link>
          <Link
            to="/operazioni-immobiliari"
            data-testid="double-cta-proponi"
            className="group border border-risco-ink bg-risco-ink text-risco-bg hover:bg-risco-accent hover:border-risco-accent p-10 md:p-14 rounded-sm transition-colors flex flex-col"
          >
            <div className="text-xs uppercase tracking-[0.28em] text-risco-accent group-hover:text-risco-bg mb-6">Proponi il tuo affare</div>
            <h3 className="font-heading text-3xl md:text-5xl tracking-tight">Trasforma il tuo immobile in <span className="font-display italic">un'operazione</span></h3>
            <p className="mt-6 text-stone-400 group-hover:text-stone-100 max-w-md">Un palazzo, un appartamento maltenuto, un terreno? Proponicelo. Partnership, permuta o vendita diretta.</p>
            <div className="mt-auto pt-10 flex items-center gap-3 text-sm font-medium">
              Fai la tua proposta <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
